import Edit16 from '@carbon/icons-react/es/edit/16';
import TrashCan16 from '@carbon/icons-react/es/trash-can/16';
import { Tooltip } from 'antd';
import React, { FC, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { IdpType } from 'api/account';
import { SamlConfig } from 'api/security/idp';
import { Menu } from 'components/atoms/dropdown';
import { confirm } from 'components/atoms/modal';
import { MoreActionsDropdown } from 'components/atoms/more-actions';
import { Switch } from 'components/atoms/switch/switch';
import { toastService } from 'components/molecules/toast';
import { TranslationKey, useTranslate } from 'hooks/use-translation';
import { useQueryIdps, useSamlDelete, useSamlUpdate } from 'queries/saml';
import { SubCategoryWrapper, SubTitle } from './component';
import { SamlFormPage, SetupSamlDrawer } from './setup-saml-drawer';

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  justify-items: center;
  gap: var(--spacing-04);
`;

const Wrapper = styled.div`
  display: flex;
  gap: var(--spacing-03);
  .ant-dropdown-trigger {
    align-self: center;
  }
`;

const StyledLink = styled(SubTitle)`
  color: var(--blue-60) !important;
  cursor: pointer;
`;

const IdpWrapper = styled.div`
  flex-grow: 1;
`;

export const SSOList: FC = () => {
  const { data: idps, isLoading } = useQueryIdps();
  const { mutateAsync } = useSamlUpdate();
  const { mutateAsync: deleteIdp } = useSamlDelete();
  const [editInfo, setEditInfo] = useState<{
    idpId?: string;
    enableEdit: boolean;
    page?: SamlFormPage;
  }>({
    idpId: undefined,
    enableEdit: false,
    page: undefined,
  });
  const translate = useTranslate();

  const onDisableEdit = () => {
    setEditInfo({
      enableEdit: false,
      idpId: undefined,
      page: undefined,
    });
  };
  const onEnableEdit = (idpId) => {
    setEditInfo({
      enableEdit: true,
      idpId,
      page: SamlFormPage.SETUP,
    });
  };
  const showMetaPage = (idpId) => {
    setEditInfo({
      enableEdit: true,
      idpId,
      page: SamlFormPage.META,
    });
  };
  const deleteSamlConfig = (idpId) =>
    deleteIdp(idpId, {
      onSuccess: () =>
        toastService({
          title: translate('config.deleted'),
          message: translate('settings.saml.deleted.body'),
        }).success(),
    });

  const onDelete = (idpId) => {
    confirm({
      title: translate('settings.saml.delete.title'),
      content: translate('settings.saml.delete.body', {
        strong: (chunk) => <strong>{chunk}</strong>,
      }),
      okText: translate('delete'),
      cancelText: translate('cancel'),
      okType: 'danger',
      onOk: async () => deleteSamlConfig(idpId),
    });
  };

  if (!idps?.length) {
    return null;
  }

  const showToast = (enabled) => {
    const prefix = 'setup_profile.sso';
    const suffix = enabled ? 'enabled' : 'disabled';

    toastService({
      title: translate(`${prefix}.title.${suffix}` as TranslationKey),
      message: translate(`${prefix}.message.${suffix}` as TranslationKey),
    }).success();
  };

  const enableIdp = (idp: SamlConfig, enabled: boolean) => {
    return mutateAsync(
      { enabled, idpId: idp.idpId },
      {
        onSuccess: () => showToast(enabled),
      },
    );
  };

  const onChange = (idp: SamlConfig, enabled: boolean) => {
    if (enabled) {
      confirm({
        title: translate('settings.security.saml.model.title'),
        content: translate('settings.security.saml.model.body'),
        okText: translate('settings.security.saml.model.ok'),
        cancelText: translate('settings.security.saml.model.cancel'),
        onOk: () => enableIdp(idp, enabled),
        closable: true,
      });

      return;
    }
    confirm({
      title: translate('setup_profile.sso.disabled.confirm.title'),
      content: translate('setup_profile.sso.disabled.confirm.body'),
      okText: translate('disable'),
      cancelText: translate('cancel'),
      onOk: async () => enableIdp(idp, enabled),
      okType: 'danger',
    });
  };

  return (
    <SubCategoryWrapper>
      <TitleWrapper>
        <SubTitle>
          <FormattedMessage id="setings.sso.title" />
        </SubTitle>
        {idps.map((idp) => (
          <Wrapper>
            <Tooltip
              title={translate(
                idp.enabled
                  ? 'setup_profile.sso.tootlip.enable'
                  : 'setup_profile.sso.tootlip.disable',
              )}
            >
              <Switch
                size="small"
                checked={idp.enabled}
                onChange={(enable) => onChange(idp, enable)}
                disabled={isLoading}
              />
            </Tooltip>
            <IdpWrapper>
              <SubTitle $noMargin>
                {idp.idpType === IdpType.Saml ? (
                  <FormattedMessage id="settings.saml.title" />
                ) : (
                  idp.idpType
                )}
              </SubTitle>
              <StyledLink onClick={() => showMetaPage(idp.idpId)}>
                <FormattedMessage id="settings.saml.wiki-how" />
              </StyledLink>
            </IdpWrapper>
            <MoreActionsDropdown
              menu={
                <Menu>
                  <Menu.Header>
                    <span>
                      <FormattedMessage id="more_actions" />
                    </span>
                  </Menu.Header>
                  <Menu.ItemEdit
                    icon={<Edit16 />}
                    onClick={() => onEnableEdit(idp.idpId)}
                  />
                  <Menu.Divider />
                  <Menu.ItemDelete
                    icon={<TrashCan16 />}
                    onClick={() => onDelete(idp.idpId)}
                  />
                </Menu>
              }
            />
          </Wrapper>
        ))}
      </TitleWrapper>
      <SetupSamlDrawer
        idpId={editInfo.idpId}
        onClose={onDisableEdit}
        visible={editInfo.enableEdit}
        page={editInfo.page}
      />
    </SubCategoryWrapper>
  );
};
